import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { UserPlus, Eye, EyeSlash } from '@phosphor-icons/react';
import { toast } from 'sonner';

export const Register = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState('');
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { register, login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreedTerms) {
      toast.error('Please accept the terms before creating an account');
      return;
    }
    setIsLoading(true);
    try {
      await register(email, password, name);
      toast.success('Welcome aboard. Signing you in…');
      await login(email, password);
      navigate('/', { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full min-h-screen flex items-center justify-center bg-background px-4 py-10" data-testid="register-page">
      <div className="w-full max-w-md p-10 panel-luxe animate-fade-up" data-testid="register-form">
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-10 w-14 rounded-md border border-border bg-stone flex items-center justify-center">
              <span className="font-serif text-gold text-xl leading-none tracking-tight">MP</span>
            </div>
            <span className="text-xs uppercase tracking-[0.32em] text-ash">Manuscript Polish</span>
          </div>
          <h1 className="text-4xl font-serif font-light tracking-tight text-ivory">Create an account.</h1>
          <p className="text-sm text-bone mt-3 font-sans leading-relaxed">Begin with 3,000 free words on the house.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash block mb-2">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full px-4 py-3 rounded-lg border border-border bg-background text-ivory placeholder:text-ash focus:border-gold focus:ring-1 focus:ring-gold/40 transition-colors outline-none font-sans"
              data-testid="name-input"
            />
          </div>
          <div>
            <label className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash block mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-3 rounded-lg border border-border bg-background text-ivory placeholder:text-ash focus:border-gold focus:ring-1 focus:ring-gold/40 transition-colors outline-none font-sans"
              data-testid="email-input"
            />
          </div>
          <div>
            <label className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash block mb-2">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="w-full pl-4 pr-11 py-3 rounded-lg border border-border bg-background text-ivory placeholder:text-ash focus:border-gold focus:ring-1 focus:ring-gold/40 transition-colors outline-none font-sans"
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-ash hover:text-gold p-1"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                title={showPassword ? 'Hide password' : 'Show password'}
                data-testid="toggle-password-visibility"
              >
                {showPassword ? <EyeSlash size={18} weight="duotone" /> : <Eye size={18} weight="duotone" />}
              </button>
            </div>
            <p className="text-[11px] text-ash mt-2">Minimum 6 characters</p>
          </div>

          <div className="rounded-lg border border-border bg-stone/40 p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={agreedTerms}
                onChange={(e) => setAgreedTerms(e.target.checked)}
                className="mt-1 h-4 w-4 accent-gold cursor-pointer"
                data-testid="agree-terms-checkbox"
              />
              <span className="text-xs text-bone font-sans leading-relaxed">
                I agree to the Manuscript Polish terms. <span className="text-ivory">Accounts are for personal use.</span>
                {' '}Your 3,000-word free trial is one per person — sharing a login across multiple users or devices in
                a short window may flag the account, suspend free credits, and require email verification on each new
                sign-in location.
              </span>
            </label>
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full rounded-lg font-medium text-sm tracking-wide px-6 py-3 transition-colors duration-200 bg-gold text-charcoal hover:bg-gold-dark border border-transparent"
            data-testid="register-submit-btn"
          >
            <UserPlus size={16} weight="duotone" className="mr-2" />
            {isLoading ? 'Creating account…' : 'Create Account'}
          </Button>
        </form>

        <div className="mt-8">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-[10px] uppercase">
              <span className="bg-card px-3 text-ash tracking-[0.32em]">or</span>
            </div>
          </div>

          <Button
            type="button"
            onClick={loginWithGoogle}
            className="w-full mt-5 rounded-lg font-medium text-sm tracking-wide px-6 py-3 transition-colors duration-200 bg-transparent text-ivory border border-border hover:border-gold hover:text-gold"
            data-testid="google-register-btn"
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>
        </div>

        <p className="mt-8 text-center text-sm text-bone font-sans">
          Already have an account?{' '}
          <Link to="/login" className="text-gold hover:text-gold-light underline-offset-4 hover:underline" data-testid="login-link">
            Login
          </Link>
        </p>
      </div>
    </div>
  );
};
